import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '@/lib/api';
import { getAuthoringDraft, saveAuthoringDraft } from '../api/authoringApi';
import type { AuthoringDraft, AuthoringFields } from '../types/authoring.types';
import { authoringJsonFingerprint } from '../utils/authoringContent';

export function authoringFields(draft: AuthoringFields): AuthoringFields {
  return { title: draft.title, controlCode: draft.controlCode, department: draft.department,
    language: draft.language, pageFormat: draft.pageFormat, content: draft.content };
}

const fingerprint = (draft: AuthoringFields) => authoringJsonFingerprint(authoringFields(draft));

/** A single save lane; late responses can never replace edits made while saving. */
export function useAuthoringDraft(initial: AuthoringDraft, canEdit: boolean) {
  const [draft, setDraft] = useState(initial);
  const current = useRef(initial);
  const acknowledged = useRef(initial);
  const saving = useRef<Promise<AuthoringDraft> | null>(null);
  const [state, setState] = useState<'saved' | 'unsaved' | 'saving' | 'error' | 'conflict'>('saved');
  const [error, setError] = useState<Error | null>(null);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const mutation = useMutation({
    mutationFn: (input: { fields: AuthoringFields; version: number }) => saveAuthoringDraft(initial.id, input.fields, input.version),
    retry: false,
  });
  const mutateAsync = mutation.mutateAsync;

  const update = useCallback((fields: Partial<AuthoringFields>) => {
    const next = { ...current.current, ...fields, preparedSnapshotId: null };
    if (fingerprint(next) === fingerprint(current.current)) return;
    current.current = next;
    setDraft(current.current);
    setState((previous) => previous === 'conflict' || previous === 'error' ? previous : 'unsaved');
  }, []);

  const accept = useCallback((server: AuthoringDraft, sent: AuthoringFields) => {
    acknowledged.current = server;
    current.current = fingerprint(current.current) === fingerprint(sent)
      ? server
      : { ...server, ...authoringFields(current.current), preparedSnapshotId: null };
    if (mounted.current) setDraft(current.current);
  }, []);

  const flush = useCallback(async (): Promise<AuthoringDraft> => {
    if (saving.current) return saving.current;
    if (!canEdit || fingerprint(current.current) === fingerprint(acknowledged.current)) return current.current;
    if (state === 'conflict') throw error || new Error('Concurrent edit');
    const operation = async () => {
      setError(null);
      try {
        while (fingerprint(current.current) !== fingerprint(acknowledged.current)) {
          setState('saving');
          const fields = authoringFields(current.current);
          const version = acknowledged.current.lockVersion;
          let saved: AuthoringDraft;
          try {
            saved = (await mutateAsync({ fields, version })).draft;
          } catch (saveError) {
            // A timeout may have committed. Read back before repeating a compare-and-swap.
            if (saveError instanceof ApiError && (saveError.isTimeout || saveError.isNetworkError || saveError.status === 409)) {
              const remote = await getAuthoringDraft(initial.id).catch(() => null);
              if (remote && fingerprint(remote.draft) === fingerprint(fields)) saved = remote.draft;
              else throw saveError;
            } else throw saveError;
          }
          accept(saved, fields);
        }
        if (mounted.current) setState('saved');
        return current.current;
      } catch (failure) {
        const nextError = failure instanceof Error ? failure : new Error(String(failure));
        if (mounted.current) {
          setError(nextError);
          setState(failure instanceof ApiError && failure.status === 409 ? 'conflict' : 'error');
        }
        throw failure;
      } finally { saving.current = null; }
    };
    saving.current = operation();
    return saving.current;
  }, [accept, canEdit, error, initial.id, mutateAsync, state]);

  useEffect(() => {
    if (state !== 'unsaved' || !canEdit) return;
    const timer = window.setTimeout(() => { void flush().catch(() => undefined); }, 800);
    return () => window.clearTimeout(timer);
  }, [draft, state, canEdit, flush]);

  const replaceWithServer = (server: AuthoringDraft) => {
    current.current = server;
    acknowledged.current = server;
    setDraft(server);
    setError(null);
    setState('saved');
  };

  return { draft, state, error, update, flush, replaceWithServer,
    isDirty: fingerprint(draft) !== fingerprint(acknowledged.current) || state === 'saving',
    getCurrent: () => current.current };
}
